<template>
    <table v-if="currentGroup.items.length"
           data-component="OrderItemsTable"
           data-testid="orderItemsTable"
           class="dt-table order-items-table">
        <tbody>
            <tr v-for="orderItem in currentGroup.items"
                :key="orderItem.ID"
                data-testid="orderItemLine"
                class="order-items-table__line order-item"
                :class="{
                    // Check syntax
                    'order-redeemed': orderItem.redeemed == orderItem.qty,
                    'order-items-table__line--child': !!orderItem.parentID,
                    'order-items-table__line--clone': !!orderItem.isClone
                }"
                @click.prevent="lineClick(orderItem)">
                <!-- Item Heading (mobile only) -->
                <td class="mobile-only"
                    data-testid="orderItemMobileHeading">
                    <div class="order-items-table__mobile-heading">
                        <div>
                            <!-- Option set name -->
                            <small v-if="orderItem.optionSet && orderItem.optionSet.shortName"
                                   class="dt-table__cell-subtitle">{{ orderItem.optionSet.shortName }}:</small>

                            {{ orderItem.qty }} x {{ orderItem.getName() }}
                        </div>

                        <!-- The price cell itself is hidden on mobile, so the price goes in the heading. -->
                        <div v-if="!orderItem.isClone"
                             data-testid="orderItemMobilePrice"
                             class="order-items-table__mobile-price">
                            {{ currencyFilter(orderItem.lineTotal) }}
                        </div>
                    </div>
                </td>

                <!-- Qty cell (desktop only) -->
                <td class="desktop-only dt-table__cell--narrow order-items-table__qty-cell"
                    data-testid="orderItemQtyCell">
                    <span v-if="!!orderItem.parentID"
                          data-testid="orderItemChildConnector"
                          class="order-items-table__child-connector"></span>

                    <!-- The quantity is only shown for parent lines -->
                    <!-- For child lines we show it in the name cell for better spacing -->
                    <span v-if="!orderItem.parentID && !orderItem.isClone"
                          data-testid="orderItemQty">{{ orderItem.qty }} x</span>
                </td>

                <!-- Item Name + Details Cell. The name is only shown on desktop. The additional info is for desktop + mobile. -->
                <!-- On mobile we want to hide the cell if it's empty (no additional details) -->
                <td class="order-items-table__detail-cell"
                    data-testid="orderItemDetailCell"
                    :class="{'dt-table__cell--empty-mobile': !(orderItem.item.hasSeats || showBranchNames || orderItem.isExpired() || orderItem.validUntil || orderItem.discount || (!orderItem.hasIndividualTicketInstances() && !orderItem.customFieldsVisible && orderLineHasValidFields(orderItem)) || orderItem.customFieldsVisible)}">
                    <div class="order-items-table__detail-cell-inner">
                        <!-- Option set name -->
                        <small v-if="orderItem.optionSet && orderItem.optionSet.shortName"
                               data-testid="orderItemOptionSet"
                               class="dt-table__cell-subtitle desktop-only">{{ orderItem.optionSet.shortName }}:</small>

                        <!-- Qty for child lines is shown here because if we keep it in the qty cell it pushes the name of
the parent line too far to the right due to the connector line's space too. -->
                        <span v-if="orderItem.parentID && !orderItem.isClone"
                              data-testid="orderItemChildQty"
                              class="order-items-table__child-qty desktop-only">{{ orderItem.qty }} x</span>

                        <span v-if="orderItem.itemType === 'Bundle'"
                              data-testid="orderItemBundleLabel"
                              class="desktop-only label label-neutral mr-05">{{ l('ITEM_TYPE.BUNDLE') }}</span>

                        <span class="order-items-table__name desktop-only"
                              data-testid="orderItemName">{{ orderItem.getName() }}</span>

                        <!-- Seats icon -->
                        <div v-if="orderItem.item.hasSeats"
                             data-testid="orderItemSeats"
                             class="dt-table__cell-subtitle">
                            <span v-if="orderItem.seatNumberStrings.length > 0"
                                  data-testid="orderItemSeatNumbers">
                                <i class="fa-solid fa-chair"></i>
                                {{ orderItem.seatNumberStrings.join(', ') }}
                            </span>

                            <span v-if="orderItem.unallocatedSeatCount > 0"
                                  data-testid="orderItemUnallocatedSeatsAlert"
                                  class="alert alert-warning order-items-table__unallocated-seats-alert">
                                <i class="fa-solid fa-chair"></i>
                                {{ orderItem.unallocatedSeatCount }}
                                <!-- Don't break white space: -->
                                <!-- TODO refactor this - sure we can use pluralise -->
                                <!-- @formatter:off -->
                                <strong data-testid="orderItemUnallocatedSeatsLabel">{{ (orderItem.unallocatedSeatCount === 1 ? l('ORDER_DETAILS.UNALLOCATED_SEAT') : l('ORDER_DETAILS.UNALLOCATED_SEATS')) }}</strong>&nbsp;<br />{{ l('ORDER_DETAILS.UNALLOCATED_SEATS_HELP') }}
                            <!-- @formatter:on -->
                            </span>
                        </div>

                        <!-- Item branch name -->
                        <div v-if="showBranchNames"
                             data-testid="orderItemBranchName"
                             class="dt-table__cell-subtitle">
                            <i class="fa-solid fa-shop"></i>
                            {{ orderItem.branch.name }}
                        </div>

                        <!-- Expired indicator -->
                        <div v-if="orderItem.isExpired()"
                             data-testid="orderItemExpiredIndicator">
                            <span class="indicator indicator-danger ml-0">
                                {{ l('ORDER_DETAILS.TICKET_STATUS_EXPIRED') }}
                            </span>
                        </div>

                        <!-- Valid until -->
                        <span v-if="orderItem.validUntil"
                              data-testid="orderItemValidUntil"
                              class="dt-table__cell-subtitle">
                            {{ l('ORDER_DETAILS.VALID_UNTIL') }}
                            <!-- {{ orderItem.validUntil | moment:DateFormat.DATE }} -->
                            {{ dateFilter(orderItem.validUntil) }}
                        </span>

                        <!-- Discount name -->
                        <span v-if="orderItem.discount"
                              data-testid="orderItemDiscount"
                              class="dt-table__cell-subtitle">
                            {{ l('ORDER_DETAILS.DISCOUNT') }} : {{ orderItem.discount.getDescription() }}
                            ({{ currencyFilter(orderItem.discount.amount) }})
                        </span>

                        <!-- Show field data button -->
                        <div v-if="!orderItem.hasIndividualTicketInstances() && !orderItem.customFieldsVisible && orderLineHasValidFields(orderItem)"
                             class="order-items-table__show-fields-btn">
                            <button :class="{'active': orderItem.customFieldsVisible }"
                                    data-testid="orderItemShowFieldsBtn"
                                    type="button"
                                    class="btn btn-default btn-sm"
                                    @click.prevent="orderItem.toggleCustomFieldsVisibility()">
                                {{ l('ORDER_DETAILS.SHOW_FIELDS_BTN') }} <i class="fa-solid fa-list"></i>
                            </button>
                        </div>

                        <!-- Item level data for all instances on this line -->
                        <div class="order-line-field-data"
                             data-testid="orderItemFieldData"
                             :class="{ 'visible-list' : orderItem.customFieldsVisible }">
                            <!-- From new item level instance data -->
                            <div v-for="itemInstance in orderItem.iteminstances"
                                 :key="itemInstance.ID"
                                 data-testid="orderItemInstanceFieldData">
                                <span class="instance-number">{{ itemInstance.instance }}</span>
                                <ul class="one-instance-field-data">
                                    <!-- TODO refactor - we can use a computed to combine order + v-if -->
                                    <!-- | orderBy : 'field.navOrder' -->
                                    <template v-for="instance in itemInstance.fields.instances"
                                              :key="instance.ID">
                                        <li v-if="instance.hasValue"
                                            data-testid="orderItemFieldLine">
                                            <strong>{{ instance.field.name }}:</strong>
                                            <FieldInstance :instance="instance" />
                                        </li>
                                    </template>
                                </ul>
                            </div>
                        </div>
                    </div>
                </td>

                <!-- Price Cell -->
                <td v-show="!orderItem.isClone"
                    data-testid="orderItemPriceCell"
                    class="order-items-table__price-cell desktop-only">
                    {{ currencyFilter(orderItem.lineTotal) }}

                    <!-- Booking Fee -->
                    <span v-if="orderItem.bookingFee"
                          data-testid="orderItemBookingFee"
                          class="dt-table__cell-subtitle desktop-only">
                        {{ l('ORDER_DETAILS.BOOKING_FEE', {
                            qty: orderItem.qty,
                            bookingFee: orderItem.bookingFee
                        }) }}
                    </span>

                    <!-- Donation -->
                    <span v-if="orderItem.donation"
                          data-testid="orderItemDonation"
                          class="dt-table__cell-subtitle desktop-only">
                        {{ l('ORDER_DETAILS.DONATION', {
                            qty: orderItem.qty,
                            bookingFee: orderItem.donation
                        }) }}
                    </span>
                </td>

                <td v-show="orderItem.isClone"
                    data-testid="orderItemClonePriceCell"
                    class="order-items-table__price-cell desktop-only">
                <!-- We can't just go hiding table cells so insert an empty one -->
                </td>

                <!-- Redeem Cell -->
                <td v-if="currentGroup.isRedemptionPossible?.() && currentOrder.isLive()"
                    data-testid="orderItemRedemptionCell"
                    class="order-items-table__redemption-cell">
                    <div v-if="orderItem.isAdjusted()"
                         data-testid="orderItemAdjustedMessage">
                        {{ l('ORDER_DETAILS.LINE_REPLACED') }}
                    </div>

                    <div v-if="orderItem.isAdjustment()"
                         data-testid="orderItemAdjustmentMessage">
                        {{ l('ORDER_DETAILS.LINE_REVERSES') }}
                    </div>

                    <div class="redemption-boxes-container">
                        <!-- redemption-boxes-container is display:flex. -->
                        <!-- This div is to keep everything grouped together within in -->
                        <div>
                            <!-- Redemption box for non-individual instances, with redeem all button -->
                            <div v-if="!orderItem.hasIndividualTicketInstances() && orderItem.isTicket()"
                                 data-testid="orderItemLineRedemptionBoxWrapper">
                                <LineRedemptionBox :order-item="orderItem" />
                            </div>

                            <!-- Redemption box per each individual instance ticket -->
                            <div v-if="orderItem.hasIndividualTicketInstances() && orderItem.isTicket()"
                                 data-testid="orderItemInstanceRedemptionBoxWrapper">
                                <template v-for="(itemInstance, index) in orderItem.iteminstances"
                                          :key="itemInstance.ID">
                                    <InstanceRedemptionBox :current-order="currentOrder"
                                                           :order-item="orderItem"
                                                           :item-instance="itemInstance"
                                                           :item-instance-index="index" />
                                </template>
                            </div>

                            <!-- Membership Options -->
                            <div v-if="orderItem.isMembershipPlan()"
                                 data-testid="orderItemMembershipsWrapper"
                                 class="order-details-memberships">
                                <template v-for="itemInstance in orderItem.iteminstances"
                                          :key="itemInstance.ID">
                                    <!-- TODO: this is not very elegant -->
                                    <div v-if="itemInstance.getMembership()"
                                         data-testid="orderItemMembership">
                                        <OrderDetailsMembership :current-group="currentGroup"
                                                                :order-item="orderItem"
                                                                :item-instance="itemInstance" />
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>
                </td>
            <!-- End Redemption Cell -->
            </tr>
        </tbody>
    </table>
</template>

<script>
import FieldInstance from '@/epos/vue/pages/order-details/components/FieldInstance';
import InstanceRedemptionBox from '@/epos/vue/pages/order-details/components/InstanceRedemptionBox';
import LineRedemptionBox from '@/epos/vue/pages/order-details/components/LineRedemptionBox';
import OrderDetailsMembership from '@/epos/vue/pages/order-details/components/OrderDetailsMembership';
import { isGroupRedeemableCheck } from '@/epos/vue/pages/order-details/functions/redemptionFunctions';
import { mapState } from 'vuex';

export default {
    components: {
        FieldInstance,
        OrderDetailsMembership,
        InstanceRedemptionBox,
        LineRedemptionBox,
    },

    props: {
        // Todo can we just fetch this from vuex too?
        currentOrder: {
            type: Object,
            required: true,
        },

        currentGroup: {
            type: Object,
            required: true,
        },
    },

    computed: {
        ...mapState('orderDetails', [
            'currentOrderIsCrossBranch',
        ]),
        isGroupRedeemable() {
            return isGroupRedeemableCheck(this.currentGroup, this.currentOrder.isLive());
        },
        showBranchNames() {
            return this.currentOrderIsCrossBranch;
        },
    },

    methods: {
        /**
         * If this is a bundle (cloned orderItem), switch to the tab for the bundle's own itemType.
         * We can miss out OrderDetailCtrl because Vue manages the currentTab directly.
         *
         * @param orderItem
         * @see orderItemGroups.js
         */
        lineClick(orderItem) {
            if (orderItem.isClone) {
                this.$store.commit('orderDetails/setCurrentTab', orderItem.itemType);
            }
        },

        orderLineHasValidFields(orderLine) {
            for (let i = 0; i < orderLine.iteminstances.length; i++) {
                for (let j = 0; j < orderLine.iteminstances[i].fields.instances.length; j++) {
                    const fieldInstance = orderLine.iteminstances[i].fields.instances[j];
                    if (fieldInstance.hasValue && !fieldInstance.error) {
                        return true;
                    }
                }
            }

            return false;
        },
    },
};
</script>
